
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Activity, Bell, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ProfessionalToolSection() {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-indigo-50 rounded-br-[100px] opacity-70"></div>
      <div className="absolute bottom-0 right-0 w-1/4 h-1/4 bg-blue-50 rounded-tl-[100px] opacity-70"></div>
      
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Image */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <div className="aspect-w-4 aspect-h-3">
                <img 
                  src="https://gbmarketplacestorage.blob.core.windows.net/sleepnest/analyst.png" 
                  alt="Sleep specialist analyzing data" 
                  className="object-cover w-full h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/40 to-transparent"></div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-indigo-600" />
                  <span className="text-sm font-medium text-gray-800">Sleep Pattern Analysis</span>
                </div>
              </div>
              
              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg max-w-[180px]">
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    <span className="text-xs font-medium text-gray-800">AI Insight</span>
                  </div>
                  <p className="text-xs text-gray-600">Consistent early morning wakings detected</p>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-purple-100 rounded-full filter blur-xl opacity-80"></div>
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-blue-100 rounded-full filter blur-xl opacity-80"></div>
          </motion.div>
          
          {/* Right Column - Content */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                <span>For Pediatric Sleep Coaches</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                The Professional Tool Designed for Pediatric Sleep Coaches
              </h2>
              
              <p className="text-lg text-gray-700">
                Unlike parent-focused apps, SleepNest is designed for professionals. We help pediatric sleep coaches analyze sleep, nap, and feeding data—so you can provide faster, data-backed recommendations to exhausted families.
              </p>
            </div>
            
            <div className="space-y-5">
              {[
                {
                  icon: <Activity className="h-5 w-5 text-indigo-600" />,
                  title: "AI-powered sleep analysis",
                  description: "Identify disruptions without manual sorting."
                },
                {
                  icon: <Bell className="h-5 w-5 text-indigo-600" />,
                  title: "Real-time updates from parents",
                  description: "No chasing clients for information."
                },
                {
                  icon: <Sparkles className="h-5 w-5 text-indigo-600" />,
                  title: "Pattern recognition tools",
                  description: "See clear trends between feeding, naps, and sleep behavior."
                }
              ].map((feature, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                  className={cn(
                    "flex items-start space-x-4 p-4 rounded-xl transition-colors",
                    "bg-white border border-gray-100 shadow-sm hover:bg-gray-50"
                  )}
                >
                  <div className="flex-shrink-0 bg-indigo-100 p-2 rounded-lg">
                    {feature.icon}
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-medium text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}